import { useCallback } from "react";
import { openNotificationThunk } from "@store/Notification/thunks";
import { useAppDispatch } from "@app/hooks";

// Matches the Investor Request Form allow-list (LPC-10865).
const allowedExtensions = ["png", "jpg", "jpeg", "bmp", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "pdf"];

// LPC-10865: 25 MB cumulative cap across all attachments. Binary MB matches
// the Windows file-size display and the contact-service backend cap.
const MAX_TOTAL_SIZE_BYTES = 25 * 1024 * 1024;

const useAttachmentValidator = () => {
  const dispatch = useAppDispatch();

  /** Returns true when `file` passes type + cumulative-size checks; otherwise dispatches a warning. */
  const validateAttachment = useCallback(
    (file: File, currentTotalBytes = 0) => {
      const fileExtension = file.name.split(".").pop();

      if (!fileExtension || !allowedExtensions.includes(fileExtension.toLowerCase())) {
        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: "Attachment failed. File type is not allowed. Make sure your file ends with .png, .jpg, .jpeg, .bmp, .doc, .docx, .xls, .xlsx, .ppt, .pptx, or .pdf.",
          })
        );

        return false;
      }

      if (currentTotalBytes + file.size > MAX_TOTAL_SIZE_BYTES) {
        dispatch(
          openNotificationThunk({
            variant: "warning",
            text: "Attachment failed. File size exceeds 25MB.",
          })
        );

        return false;
      }

      return true;
    },
    [dispatch]
  );

  return {
    validateAttachment,
    allowedExtensions,
  };
};

export default useAttachmentValidator;
